'use client'

import { useEffect } from 'react'
import { Alert, Box, Button, Container, Paper, Typography } from '@mui/material'
import { toast } from 'react-toastify'

// In a real production app, you would use a proper logging service
// like Sentry, LogRocket, or a custom solution that sends errors to your backend
const logErrorToService = (error: Error & { digest?: string }) => {
  // This is where you would integrate with your error logging service
  // For now, we'll just log to console in production, but in a real app
  // you would send this to your error tracking service
  if (process.env.NEXT_PUBLIC_APP_ENV === 'production') {
    // Example of what you might send to a logging service
    const errorData = {
      message: error.message,
      stack: error.stack,
      digest: error.digest,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : '',
    }

    // In production, you would send this to your logging service
    // For example: sentryClient.captureException(error, { extra: errorData })
    console.error('Production error:', errorData)
  } else {
    // In development, just log to console
    console.error('Development error:', error)
  }
}

export default function Error({ error, reset }: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const isDevelopment = process.env.NEXT_PUBLIC_APP_ENV !== 'production'

  useEffect(() => {
    // Log the error to our service
    logErrorToService(error)

    // Show a toast notification
    toast.error('Ein Fehler ist aufgetreten. Bitte versuche es erneut.')
  }, [error])

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 4, 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center',
          borderRadius: 2
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom>
          Etwas ist schiefgelaufen
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
          Bitte versuche es erneut. Sollte der Fehler bestehen bleiben, wende dich an den Support.
        </Typography>

        {isDevelopment && error.digest && (
          <Alert severity="error" role="alert" sx={{ mb: 4, width: '100%' }}>
            Fehler-ID: {error.digest}. Die vollständigen Details stehen im Server-Log.
          </Alert>
        )}

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={() => reset()}
            size="large"
          >
            Erneut versuchen
          </Button>

          <Button 
            variant="outlined" 
            color="primary" 
            onClick={() => window.location.href = '/'}
            size="large"
          >
            Zur Startseite
          </Button>
        </Box>

        {isDevelopment && error.digest && (
          <Box sx={{ mt: 4, width: '100%' }}>
            <Typography variant="subtitle2" component="h3" gutterBottom>
              Diagnose
            </Typography>
            <Box component="pre" sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, overflow: 'auto', color: '#000' }}>
              {JSON.stringify({ digest: error.digest }, null, 2)}
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  )
}
